<template>
  <div
    class="combo-poster"
    :style="{
      backgroundImage: `url(${backgroundUrl})`,
      width: `${posterWidth}px`,
    }"
  >
    <header class="poster-header">
      <img :src="logo" alt="OK租机" />
      <p>租机上OK · 万事都OK</p>
    </header>
    <div class="poster-customer">
      <i class="el-icon-user" /> <span>客户姓名</span
      ><strong>{{ maskedCustomerName }}</strong>
      <i class="el-icon-phone-outline poster-mobile-icon" />
      <span>手机号</span>
      <strong>{{ maskedCustomerMobile }}</strong>
    </div>
    <div v-for="group in groups" :key="group.prepayRate" class="poster-group">
      <div class="poster-cards">
        <section
          v-for="combo in group.list"
          :key="combo.comboId"
          class="poster-card"
          :class="{ 'is-disabled': combo.selectable === false }"
        >
          <span class="poster-number">方案{{ combo.index }}</span>
          <div class="poster-card-customer">
            <span>客户姓名</span><strong>{{ maskedCustomerName }}</strong>
          </div>
          <h3>
            {{
              (combo.comboShowTitle || "")
                .split(/[|｜]/)
                .filter((part) => !part.includes("费率"))
                .join("|")
                .replace("首付", "首期")
            }}
          </h3>
          <div v-if="showRetailPrice" class="poster-row poster-retail-price">
            <strong>零售价</strong
            ><span class="poster-money">{{ money(salePrice) }}</span>
          </div>
          <div class="poster-row">
            <strong>首期租金</strong
            ><span class="poster-money">{{ money(combo.prepayPrice) }}</span>
          </div>
          <div class="poster-row secondary">
            <span>设备管理费</span><span>{{ money(combo.lockPrice) }}</span>
          </div>
          <div class="poster-divider" />
          <div class="poster-row">
            <strong>剩余租金</strong
            ><span class="poster-money">{{ money(combo.leftRentPrice) }}</span>
          </div>
          <div
            v-for="(bill, index) in combo.leftBillList"
            :key="index"
            class="poster-row bill-row"
          >
            <span>{{ bill.billName }}</span
            ><span>{{ money(bill.leftPayAmount) }}</span>
          </div>
        </section>
      </div>
    </div>
  </div>
</template>

<script>
import logo from "@/assets/logo.png";
import html2canvas from "html2canvas";
import { prepareScreenshot } from "./prepare-screenshot";

export default {
  name: "ComboScreenshotPoster",
  props: {
    groups: { type: Array, required: true },
    customerName: { type: String, default: "" },
    customerMobile: { type: String, default: "" },
    showRetailPrice: Boolean,
    salePrice: { type: [String, Number], default: "" },
  },
  data() {
    return {
      logo,
      backgroundUrl: "/static/combo-screenshot-background.png",
    };
  },
  computed: {
    maskedCustomerMobile() {
      return this.customerMobile.trim().replace(/(\d{3})\d{4}(\d{4})$/, "$1****$2") || "-";
    },
    maskedCustomerName() {
      const characters = Array.from(this.customerName.trim());
      if (!characters.length) return "-";
      return characters.length === 1
        ? "*"
        : characters[0] + "*".repeat(Math.max(1, characters.length - 2)) + characters[characters.length - 1];
    },
    posterWidth() {
      const columns = Math.max(
        2,
        ...this.groups.map((group) => group.list.length),
      );
      return 80 + columns * 360 + (columns - 1) * 20;
    },
  },
  methods: {
    async toBlob() {
      const canvas = await html2canvas(this.$el, {
        scale: 2,
        backgroundColor: "#f5f7fa",
        onclone: prepareScreenshot,
      });
      return new Promise((resolve, reject) =>
        canvas.toBlob((blob) => {
          if (blob) resolve(blob);
          else reject(new Error("截图生成失败"));
        }, "image/png"),
      );
    },
    money(value) {
      if (value === "" || value == null) return "-";
      const amount = Number(value);
      return Number.isFinite(amount)
        ? `￥${amount.toLocaleString("zh-CN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
        : "-";
    },
  },
};
</script>

<style lang="scss" scoped>
.combo-poster {
  box-sizing: border-box;
  min-height: 963px;
  padding: 40px;
  background-color: #f5f7fa;
  background-size: 100% 100%;
  color: #26324f;
  font-family: "Microsoft YaHei", "PingFang SC", sans-serif;
  font-size: 14px;
}
.poster-header {
  padding-bottom: 20px;
  border-bottom: 1px solid #e1e7ef;
  img {
    display: block;
    width: 150px;
    height: auto;
  }
  p {
    margin: 4px 0 0 43px;
    color: #8793aa;
    font-size: 14px;
  }
}
.poster-customer {
  display: flex;
  width: max-content;
  align-items: center;
  padding: 12px 16px;
  margin: 20px 0 32px;
  border-radius: 16px;
  background: #fff;
  box-shadow: 0 4px 16px rgba(38, 50, 79, 0.08);
  i {
    margin-right: 12px;
    color: #24cf91;
    font-size: 22px;
  }
  span {
    margin-right: 12px;
    color: #8490aa;
  }
  .poster-mobile-icon {
    margin-left: 24px;
  }
}
.poster-cards {
  display: flex;
  align-items: stretch;
}
.poster-card {
  position: relative;
  box-sizing: border-box;
  width: 360px;
  flex-shrink: 0;
  margin: 0 20px 32px 0;
  padding: 20px;
  border: 1px solid #e5eaf3;
  border-radius: 16px;
  background: #fff;
  box-shadow: 0 4px 18px rgba(38, 50, 79, 0.12);
  &:last-child {
    margin-right: 0;
  }
  h3 {
    margin: 0 0 20px;
    text-align: center;
    color: #26324f;
    font-size: 18px;
    font-weight: 700;
  }
  &.is-disabled {
    background: #f5f5f5;
    box-shadow: none;
    color: #a8abb2;
    h3,
    .poster-number,
    .poster-money,
    .poster-card-customer span,
    .secondary,
    .bill-row {
      color: #a8abb2;
    }
    .poster-number {
      background: #f5f5f5;
    }
    .poster-retail-price,
    .poster-retail-price .poster-money {
      color: #f56c6c;
    }
  }
}
.poster-card-customer {
  margin-bottom: 10px;
  line-height: 22px;
  span {
    margin-right: 12px;
    color: #8490aa;
  }
}
.poster-number {
  position: absolute;
  top: -14px;
  left: 20px;
  padding: 3px 16px;
  border: 1px solid #e1e7ef;
  border-radius: 6px;
  background: #f5f7fa;
  color: #303133;
  font-weight: 700;
}
.poster-row {
  display: flex;
  justify-content: center;
  align-items: baseline;
  column-gap: 26px;
  margin: 10px 0;
  line-height: 20px;
  > :first-child {
    width: 84px;
    flex-shrink: 0;
    text-align: right;
  }
  > :last-child {
    flex: 0 0 84px;
  }
}
.poster-money {
  color: #009969;
  font-size: 18px;
}
.secondary,
.bill-row {
  color: #46536b;
}
.poster-divider {
  margin: 12px 0;
  border-top: 1px solid #e1e7ef;
}
.bill-row {
  margin: 6px 0;
  font-size: 13px;
}
</style>
